import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, X, Check } from "lucide-react";
import { Label } from "@/components/ui/label";

interface ImportStatementDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => void;
}

export function ImportStatementDialog({
  open,
  onClose,
  onImport,
}: ImportStatementDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleImport = () => {
    if (selectedFile) {
      onImport(selectedFile);
      setSelectedFile(null);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-darkblue border border-gray-700/50 rounded-lg p-0">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <DialogTitle className="text-xl font-bold text-white">Importar Extrato Bancário</DialogTitle>
            <button
              onClick={handleClose}
              className="flex items-center gap-2 text-gray-400 text-sm font-medium hover:text-white transition-colors"
            >
              Fechar
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Upload Area */}
          <div className="flex flex-col gap-3 mb-6">
            <Label className="text-gray-200 text-sm font-semibold">Arquivo</Label>
            <label className="flex flex-col items-center justify-center w-full h-40 border border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-emerald-500/50 transition-colors group">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                  <Upload className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-center">
                  <p className="text-gray-300 text-sm font-medium mb-1">
                    {selectedFile ? selectedFile.name : "Clique para selecionar"}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Formatos: CSV, XLSX, OFX
                  </p>
                </div>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.ofx"
                onChange={handleFileChange}
              />
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              className="h-10 px-6 border-gray-600 text-gray-300 hover:bg-gray-700/50 rounded-lg font-medium text-sm"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImport}
              disabled={!selectedFile}
              className="h-10 px-6 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg gap-2 font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              Importar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
